import * as React from 'react';
import { t } from '@moovly/i18n';
import { Button, RadioButton, Spinner, ModalContainer } from '@moovly/ui';
import useLicenses from '../hooks/useLicenses';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store/rootStore';
import LicenseSlice, { orderLicense } from '../store/licenses.store';
import { DefaultModalProps } from '../../../types/modal';
import { useSetting } from '@moovly/moovly-settings';

interface PurchaseSubscriptionModalProps extends DefaultModalProps {}

const PurchaseSubscriptionModal: React.FC<PurchaseSubscriptionModalProps> = ({ setOpen, closeModal }) => {
  const dispatch = useDispatch<any>();
  const { contracts, isLoading: contractsLoading } = useLicenses();
  const [contractId, setContractId] = React.useState<string | undefined>(
    !!contracts.length ? contracts[0].id : undefined
  );
  const [quantity, setQuantity] = React.useState<number>(1);
  const payment = useSelector((state: RootState) => state.licenses.payment);
  const handleOrderLicense = React.useCallback(
    (contractId: string, amount: number, consentedWarning: boolean) => {
      dispatch(
        orderLicense({
          contractId,
          amount,
          consentedWarning,
        })
      );
    },
    [dispatch]
  );
  React.useEffect(() => {
    if (payment.success) {
      dispatch(LicenseSlice.actions.resetPayment());
      closeModal();
    }
  }, [closeModal, dispatch, payment]);

  const handleClose = React.useCallback(() => {
    dispatch(LicenseSlice.actions.resetPayment());

    closeModal();
  }, [closeModal, dispatch]);

  const modalStyle = { content: { maxWidth: 650 } };
  const handleOnPurchase = React.useCallback(() => {
    if (payment.warning) {
      return handleOrderLicense(contractId!, quantity!, true);
    } else {
      return handleOrderLicense(contractId!, quantity!, false);
    }
  }, [payment, contractId, quantity, handleOrderLicense]);
  const handleChangeQuantity = React.useCallback(
    ({ currentTarget }: React.ChangeEvent<HTMLInputElement>) => setQuantity(parseFloat(currentTarget.value)),
    []
  );
  const handleSelectContract = React.useCallback(
    ({ currentTarget }: React.ChangeEvent<HTMLInputElement>) => setContractId(currentTarget.value),
    []
  );
  const canPurchaseGroupLicense = useSetting('license.purchase_group_license');
  const cantBuyContracts = React.useMemo(
    () => (!contracts.length && !contractsLoading) || canPurchaseGroupLicense === 'deny',
    [contracts, contractsLoading, canPurchaseGroupLicense]
  );
  return (
    <ModalContainer
      setOpen={setOpen}
      title={t('purchase.title')}
      description={
        cantBuyContracts
          ? t('purchase.contactSales')
          : payment.error ||
            (payment.warning && t('purchase.licensesAvailable.description')) ||
            t('purchase.description')
      }
      onConfirm={cantBuyContracts ? undefined : handleOnPurchase}
      closeButtonText={payment.error || cantBuyContracts ? t('actions.close') : t('actions.cancel')}
      confirmButtonText={payment.warning ? t('actions.proceed') : t('actions.purchase')}
      confirmDisabled={payment.loading || !contractId}
      onClose={handleClose}
      style={modalStyle}
    >
      {cantBuyContracts ? (
        <></>
      ) : (
        <>
          {(payment.loading || payment.success) && <Spinner />}
          {!payment.error && !payment.warning && !payment.success && (
            <>
              <div className="row">
                <div className="col-md-8">
                  {contracts.map(({ id, license_type }) => (
                    <RadioButton
                      id={`purchase-${id}`}
                      key={`purchase-${id}`}
                      label={t(`purchase.license.optionItem`, {
                        license: t(`licenses.${license_type}`, {}, license_type),
                      })}
                      value={`${id}`}
                      checked={contractId === id}
                      onChange={handleSelectContract}
                    />
                  ))}
                </div>
                <div className="col-md-4">
                  <div className="form-floating">
                    <input
                      type="number"
                      className="form-control"
                      id="floatingInputValue"
                      value={quantity}
                      min={0}
                      onChange={handleChangeQuantity}
                    />
                    <label htmlFor="floatingInputValue">{t('purchase.license.quantity')}</label>
                  </div>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </ModalContainer>
  );
};

export default PurchaseSubscriptionModal;
